import React, { useContext, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import AppContext from 'context/Context';
import classNames from 'classnames';
import NavbarTop from 'components/navbar/top/NavbarTop';
import NavbarVertical from 'components/navbar/vertical/NavbarVertical';
import useAPI from 'hooks/useApi';
import locationAPI from 'api/Location';
import { getMonitoringLocations } from 'api/monitoringApi';
import { isEmpty, get, forIn } from 'lodash';
import { Col } from 'react-bootstrap';
import DropDownMenu from 'components/navbar/top/DropDownMenu';
import Loader from 'components/common/Loader';
import ProductProvider from 'components/app/e-commerce/ProductProvider';
import CourseProvider from 'components/app/e-learning/CourseProvider';
import { getItemFromStore } from 'helpers/utils';
import { jwtDecode } from 'jwt-decode';
import { getBlockList } from 'api/farmLevelApi';

const MainLayout = () => {
  const { hash, pathname } = useLocation();
  const isKanban = pathname.includes('kanban');
  const isEvaluate = pathname.includes('evaluate');
  const isMonitoring = pathname.includes('monitor');
  const isPlotOverlay = pathname.includes('plotoverlay');
  const { cluster } = useParams();

  const navigate = useNavigate();

  const {
    config: { isFluid, navbarPosition }
  } = useContext(AppContext);

  // const tenant = getItemFromStore(
  //   'tenant',
  //   process.env.REACT_APP_DEFAULT_TENANT
  // );
  const tokenFromLocalStorage = localStorage.getItem('token');
  let decodedToken;
  if (tokenFromLocalStorage) {
    decodedToken = jwtDecode(tokenFromLocalStorage);
  }
  const tenant = decodedToken.tenant_id;

  const { setConfig } = useContext(AppContext);
  const [locationData, setLocationData] = useState([]);
  const [monitoringLocationData, setMonitoringLocationData] = useState([]);
  const [blockLocationData, setBlockLocationData] = useState([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const [apisLoading, setApisLoading] = useState(cluster ? true : false);
  const [showDropdown, setShowDropdown] = useState(true);
  const [firstEvaluationCluster, setFirstEvaluationCluster] = useState('');
  const [firstMonitoringCluster, setFirstMonitoringCluster] = useState('');
  const [firstBlockCluster, setFirstBlockCluster] = useState('');

  useEffect(() => {
    setTimeout(() => {
      if (hash) {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }
    }, 0);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    if (pathname === '/') {
      if (tenant == '29a333e402b4e010610d5783c274c0ed') {
        navigate(`/${tenant}/dashboardAnalytics`);
      } else {
        navigate(`/${tenant}/monitor`);
      }
    }

    const substrings = ['evaluate', 'monitor', 'plotoverlay'];
    setShowDropdown(substrings.some(substring => pathname.includes(substring)));
  }, [pathname]);

  const getEvaluationLocationAPI = useAPI(locationAPI.getLocations);
  const getMonitoringLocationAPI = useAPI(getMonitoringLocations);
  const getBlockListAPI = useAPI(getBlockList);

  const apiHooks = [getEvaluationLocationAPI, getMonitoringLocationAPI];

  useEffect(() => {
    const blockData = getBlockListAPI.request(tenant);
  }, [isPlotOverlay]);

  const performSequentialAPICalls = async () => {
    setLocationLoading(true);
    for (let i = 0; i < apiHooks.length; i++) {
      await apiHooks[i].request(tenant);
    }
    setLocationLoading(false);
  };

  useEffect(() => {
    performSequentialAPICalls();
  }, []);

  useEffect(() => {
    if (!isEmpty(getEvaluationLocationAPI.data)) {
      const responseData = get(getEvaluationLocationAPI.data, 'results', {});
      const filterOptions = [];
      forIn(responseData, (value, key) => {
        filterOptions.push({ key, value });
      });
      if (!isEmpty(filterOptions)) {
        setFirstEvaluationCluster(filterOptions[0]?.key);
        isEvaluate && setConfig('cluster', filterOptions[0]?.key);
        isEvaluate && setLocationData(filterOptions);
      }
    }
    if (!isEmpty(getMonitoringLocationAPI.data)) {
      const responseData = get(getMonitoringLocationAPI.data, 'results', {});
      const filterOptions = [];
      forIn(responseData, (value, key) => {
        filterOptions.push({ key, value });
      });
      if (!isEmpty(filterOptions)) {
        setFirstMonitoringCluster(filterOptions[0]?.key);
        setMonitoringLocationData(filterOptions);
        isMonitoring && setConfig('cluster', filterOptions[0]?.key);
        isMonitoring && setLocationData(filterOptions);
      }
    }
    if (!isEmpty(getBlockListAPI.data)) {
      const responseData = get(getBlockListAPI.data, 'results', {});
      const filterOptions = [];
      forIn(responseData, block => {
        filterOptions.push({ key: block.id, value: block.name });
      });
      if (!isEmpty(filterOptions)) {
        setFirstBlockCluster(filterOptions[0]?.key);
        setBlockLocationData(filterOptions);
        isPlotOverlay && setConfig('cluster', filterOptions[0]?.key);
        isPlotOverlay && setLocationData(filterOptions);
      }
    }
  }, [
    getEvaluationLocationAPI.data,
    getMonitoringLocationAPI.data,
    getBlockListAPI.data,
    isEvaluate,
    isMonitoring,
    isPlotOverlay
  ]);

  // show loading in evaluation and monitoring pages without cluster
  if (isEvaluate || isMonitoring) {
    if (isEmpty(cluster)) {
      if (locationLoading) {
        return <Loader />;
      }
    }
  }

  return (
    <div className={isFluid ? 'container-fluid' : 'container'}>
      {(navbarPosition === 'vertical' || navbarPosition === 'combo') && (
        <NavbarVertical
          monitoringLocationData={monitoringLocationData}
          blockLocationData={blockLocationData}
          firstEvaluationCluster={firstEvaluationCluster}
          firstMonitoringCluster={firstMonitoringCluster}
          firstBlockCluster={firstBlockCluster}
        />
      )}
      <ProductProvider>
        <CourseProvider>
          <div className={classNames('content', { 'pb-0': isKanban })}>
            <NavbarTop
              dropdownDisabled={false}
              locationData={locationData}
              showDropdown={showDropdown}
              locationLoading={locationLoading}
            />
            {/* dropdown menu only in small screens */}
            {/* {showDropdown && (
              <Col className="mt-1 mb-3 d-lg-none">
                <DropDownMenu
                  locationData={locationData}
                  dropdownDisabled={apisLoading}
                  locationLoading={locationLoading}
                />
              </Col>
            )} */}

            {/*------ Main Routes ------*/}
            <Outlet context={{ setApisLoading }} />
          </div>
        </CourseProvider>
      </ProductProvider>
    </div>
  );
};

export default MainLayout;
